// Séances de soutien déclarées par le parent (jour, heure de début, heure de fin) et montant du mois :
// heures déclarées × tarif horaire de l'enseignant. Le parent déclare ; l'enseignant et
// l'administration consultent les mêmes chiffres.
import React, { useCallback, useEffect, useState } from "react";

import api from "../../services/api";
import { qlAlert, qlConfirm } from "../../utils/dialogs";
import { fcfa, liste, messageErreur } from "./soutienCommun";

const deuxChiffres = (n) => String(n).padStart(2, "0");
export const moisCourant = () => { const d = new Date(); return `${d.getFullYear()}-${deuxChiffres(d.getMonth() + 1)}`; };
const decaler = (mois, n) => {
  const [a, m] = mois.split("-").map(Number);
  const d = new Date(a, m - 1 + n, 1);
  return `${d.getFullYear()}-${deuxChiffres(d.getMonth() + 1)}`;
};
const libelleMois = (mois) => {
  const [a, m] = mois.split("-").map(Number);
  const texte = new Date(a, m - 1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  return texte.charAt(0).toUpperCase() + texte.slice(1);
};
export const duree = (minutes) => `${Math.floor(minutes / 60)} h ${deuxChiffres(minutes % 60)}`;
const dateCourte = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
const heure = (t) => String(t || "").slice(0, 5);
const aujourdhui = () => { const d = new Date(); return `${d.getFullYear()}-${deuxChiffres(d.getMonth() + 1)}-${deuxChiffres(d.getDate())}`; };

export function SelecteurMois({ mois, onChange }) {
  const suivantPossible = mois < moisCourant();
  return (
    <div className="sc-mois" role="group" aria-label="Mois affiché">
      <button type="button" className="sc-btn sc-btn--outline" onClick={() => onChange(decaler(mois, -1))} aria-label="Mois précédent">‹</button>
      <strong>{libelleMois(mois)}</strong>
      <button type="button" className="sc-btn sc-btn--outline" onClick={() => onChange(decaler(mois, 1))} disabled={!suivantPossible} aria-label="Mois suivant">›</button>
    </div>
  );
}

// Montant du mois : une ligne par demande (enseignant / parent / élève), puis le total.
export function RecapMois({ recap, colonnes = ["teacher_name"] }) {
  if (!recap) return <div className="sc-etat">Chargement…</div>;
  if (!recap.lignes.length) return <p className="sc-muted sc-petit">Aucune séance déclarée ce mois-ci.</p>;
  const titres = { teacher_name: "Enseignant", parent_name: "Parent", student_name: "Élève" };
  return (
    <div className="sc-table-defile">
      <table className="sc-table">
        <thead><tr>{colonnes.map((c) => <th key={c}>{titres[c]}</th>)}<th>Séances</th><th>Heures</th><th>Tarif horaire</th><th>Montant du mois</th></tr></thead>
        <tbody>
          {recap.lignes.map((l) => (
            <tr key={l.tutoring_request}>
              {colonnes.map((c) => <td key={c}>{l[c] || "—"}</td>)}
              <td>{l.seances}</td>
              <td>{duree(l.minutes)}</td>
              <td>{l.tarif_horaire != null ? `${fcfa(l.tarif_horaire)}/h` : "Selon la matière"}{l.tarif_manquant && <span className="sc-alerte-tarif" title="Tarif non renseigné par l'enseignant pour une matière"> ⚠</span>}</td>
              <td><strong>{fcfa(l.montant)}</strong></td>
            </tr>
          ))}
        </tbody>
        <tfoot><tr><td colSpan={colonnes.length + 1}>Total</td><td>{duree(recap.total_minutes)}</td><td></td><td><strong>{fcfa(recap.total_montant)}</strong></td></tr></tfoot>
      </table>
      {recap.lignes.some((l) => l.tarif_manquant) && <p className="sc-muted sc-petit">⚠ Tarif non renseigné par l'enseignant pour une matière : ces séances comptent 0 FCFA.</p>}
    </div>
  );
}

function useSeances(url, mois, filtre = "") {
  const [etat, setEtat] = useState({ seances: null, recap: null, erreur: "" });
  const charger = useCallback(() => {
    const params = `?month=${mois}${filtre}`;
    Promise.all([api.get(`${url}${params}`), api.get(`${url}recap/${params}`)])
      .then(([s, r]) => setEtat({ seances: liste(s.data), recap: r.data, erreur: "" }))
      .catch((e) => setEtat({ seances: [], recap: null, erreur: messageErreur(e) }));
  }, [url, mois, filtre]);
  useEffect(charger, [charger]);
  return { ...etat, charger };
}

function ListeSeances({ seances, onRetirer, avecParent = false }) {
  if (!seances?.length) return null;
  return (
    <ul className="sc-seances">
      {seances.map((s) => (
        <li key={s.id}>
          <span><strong>{dateCourte(s.date)}</strong> · {heure(s.start_time)} – {heure(s.end_time)} ({duree(s.duration_minutes)})
            {s.subject_name ? ` · ${s.subject_name}` : ""}{avecParent ? ` · ${s.parent_name}${s.student_name ? ` (${s.student_name})` : ""}` : ""}</span>
          <span className="sc-ligne">
            <span className="sc-muted">{fcfa(s.amount)}</span>
            {onRetirer && s.can_delete && <button type="button" className="sc-btn sc-btn--lien" onClick={() => onRetirer(s)}>Retirer</button>}
          </span>
        </li>
      ))}
    </ul>
  );
}

// Parent : déclarer une séance effectuée et voir le montant du mois pour cette demande.
export function SeancesParent({ demande }) {
  const [mois, setMois] = useState(moisCourant);
  const { seances, recap, erreur, charger } = useSeances("parent-teacher/sessions/", mois, `&request=${demande.id}`);
  const matieres = demande.subjects_detail || [];
  const [champs, setChamps] = useState({ date: aujourdhui(), start_time: "", end_time: "", subject: matieres[0]?.id || "" });
  const [envoi, setEnvoi] = useState(false);
  const maj = (cle) => (e) => setChamps((c) => ({ ...c, [cle]: e.target.value }));
  const minutes = (() => {
    const [h1, m1] = champs.start_time.split(":").map(Number);
    const [h2, m2] = champs.end_time.split(":").map(Number);
    return champs.start_time && champs.end_time ? h2 * 60 + m2 - (h1 * 60 + m1) : 0;
  })();
  const declarer = async (e) => {
    e.preventDefault();
    if (!champs.start_time || !champs.end_time) { qlAlert("Indiquez l'heure de début et l'heure de fin.", "error"); return; }
    setEnvoi(true);
    try {
      await api.post("parent-teacher/sessions/", { tutoring_request: demande.id, date: champs.date, start_time: champs.start_time,
        end_time: champs.end_time, subject: champs.subject || null });
      qlAlert("Séance enregistrée.", "success");
      setChamps((c) => ({ ...c, start_time: "", end_time: "" }));
      if (champs.date.slice(0, 7) !== mois) setMois(champs.date.slice(0, 7)); else charger();
    } catch (err) { qlAlert(messageErreur(err), "error"); } finally { setEnvoi(false); }
  };
  const retirer = (s) => qlConfirm("Retirer cette séance déclarée par erreur ?", async () => {
    try { await api.delete(`parent-teacher/sessions/${s.id}/`); charger(); } catch (err) { qlAlert(messageErreur(err), "error"); }
  });
  const ligne = recap?.lignes?.[0];
  return (
    <div className="sc-card sc-seances-bloc">
      <h3>Séances effectuées</h3>
      <p className="sc-muted sc-petit">Après chaque cours, indiquez le jour, l'heure de début et l'heure de fin. Le montant du mois = heures déclarées × tarif horaire de l'enseignant.</p>
      <form className="sc-seance-form" onSubmit={declarer}>
        <label>Jour<input type="date" required max={aujourdhui()} value={champs.date} onChange={maj("date")} /></label>
        <label>Début<input type="time" required step={300} value={champs.start_time} onChange={maj("start_time")} /></label>
        <label>Fin<input type="time" required step={300} value={champs.end_time} onChange={maj("end_time")} /></label>
        {matieres.length > 1 && (
          <label>Matière<select value={champs.subject} onChange={maj("subject")}>{matieres.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></label>
        )}
        <button className="sc-btn sc-btn--navy" disabled={envoi}>{envoi ? "Enregistrement…" : `Déclarer la séance${minutes > 0 ? ` (${duree(minutes)})` : ""}`}</button>
      </form>
      <div className="sc-ligne sc-entre">
        <SelecteurMois mois={mois} onChange={setMois} />
        {ligne && <span className="sc-total-mois">{duree(ligne.minutes)} × {ligne.tarif_horaire != null ? fcfa(ligne.tarif_horaire) : "tarif"}/h = <strong>{fcfa(ligne.montant)}</strong></span>}
      </div>
      {erreur ? <p className="sc-etat sc-etat--erreur">{erreur}</p> : seances === null ? <div className="sc-etat">Chargement…</div>
        : seances.length ? <ListeSeances seances={seances} onRetirer={retirer} /> : <p className="sc-muted sc-petit">Aucune séance déclarée en {libelleMois(mois).toLowerCase()}.</p>}
      <p className="sc-muted sc-petit">Une séance déclarée par erreur peut être retirée pendant 48 heures. L'enseignant et l'administration voient les mêmes séances et le même montant.</p>
    </div>
  );
}

// Enseignant et administration : séances déclarées par les parents et montant de chaque mois.
export function SeancesLecture({ url, colonnes, avecParent = true, filtre = "" }) {
  const [mois, setMois] = useState(moisCourant);
  const { seances, recap, erreur } = useSeances(url, mois, filtre);
  return (
    <div className="sc-seances-bloc">
      <SelecteurMois mois={mois} onChange={setMois} />
      {erreur ? <p className="sc-etat sc-etat--erreur">{erreur}</p> : <>
        <RecapMois recap={recap} colonnes={colonnes} />
        <ListeSeances seances={seances} avecParent={avecParent} />
      </>}
    </div>
  );
}
